import { createSlice, createSelector } from "@reduxjs/toolkit";
import { allocatePortfolios } from "../../modules/Inference/inferenceApi";



const defaultQuestionsOrdered = [
    '6e5IOH5l9fDDVNhnn0CSZO',
    '7Gpx7lbJTgQGYIEKeAVmgN',
    'jdEuZHOtcUSyhXPXU29ax',
    '97e3j1eVbzLgzDN4oVgGl',
    '3RJBYlZMNkeaNy0fIt48Cj',
    '1PYl1HctAFSVqDZimpENb2',
    '5h8q82cQBEDndLEIdsuedE'
]


const initialState = {
  // Selected path management
  selectedPathId: null,
  portfolioIds: [],
  questionIds: defaultQuestionsOrdered,

  // Stepper form state
  currentStep: 0,
  questionStates: {}, // Add this to track completion state of each question


  formData: {
    info: {
      name: null,
      birthDate: null,
      riskScore: null,
    },
    targetAllocation: 60,
    incomeFocus: null,
    useGlobal: null,
    includeGold: null,
    includeBitcoin: null,
    region: [],
    styleLean: null,
    custom_portfolio_ids: [] // Add this new field to store custom portfolio selections
  },

  // Path creation/editing state
  isEditing: false,
  isDirty: false,

  allocation: {
    loading: false,
    error: null,
    data: null,
    responses: null,
    lastUpdated: null,
    gptAnalysis: null,
    mergedPortfolios: [], // Add this to store merged portfolios

  }
};

const pathSlice = createSlice({
  name: 'path',
  initialState,
  reducers: {
    // Path selection actions
    setSelectedPath: (state, action) => {
      const { pathId, portfolioIds } = action.payload;
      state.selectedPathId = pathId;
      state.portfolioIds = portfolioIds;
      state.questionIds = action?.payload?.questionIds || defaultQuestionsOrdered;
      state.isEditing = false;
      state.isDirty = false;
    },

    updateQuestionState: (state, action) => {
      const { questionId, completed, hasComponent, value } = action.payload;
      state.questionStates[questionId] = {
        completed,
        hasComponent,
        value,
        lastUpdated: Date.now()
      };
    },

    setCurrentPath: (state, action) => {
        state.selectedPath = action.payload
    },

    clearSelectedPath: (state) => {
      state.selectedPathId = null;
      state.portfolioIds = [];
      state.isEditing = false;
      state.isDirty = false;
    },

    updatePortfolioIds: (state, action) => {
      state.portfolioIds = action.payload;
      state.isDirty = true;
    },

    // Stepper navigation
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },

    nextStep: (state) => {
      state.currentStep += 1;
    },

    previousStep: (state) => {
      state.currentStep -= 1;
    },

    // Form field updates
    updateFormField: (state, action) => {
      const { field, value } = action.payload;
      state.formData[field] = value;
      state.isDirty = true;
    },

    setFormData: (state, action) => {
      state.formData = action.payload;
      state.isDirty = true;
    },

    resetFormData: (state) => {
      state.formData = initialState.formData;
      state.currentStep = 0;
      state.questionStates = {};

      state.isDirty = false;
    },

    // Editing state
    setEditingMode: (state, action) => {
      state.isEditing = action.payload;
    },
    setMergedPortfolios: (state, action) => {
      state.allocation.mergedPortfolios = action.payload;
    },
    clearAllocationData: (state) => {
      state.allocation = initialState.allocation;
    }


  },
  extraReducers: (builder) => {
    builder
      // Handle allocation pending state
      .addCase(allocatePortfolios.pending, (state) => {
        state.allocation.loading = true;
        state.allocation.error = null;
      })
      // Handle successful allocation
      .addCase(allocatePortfolios.fulfilled, (state, action) => {
        state.allocation.loading = false;
        state.allocation.data = action.payload.data;
        state.allocation.lastUpdated = action.payload.timestamp;
        state.allocation.responses = action.payload.responses;
        state.isDirty = false;
      })
      // Handle allocation failure
      .addCase(allocatePortfolios.rejected, (state, action) => {
        state.allocation.loading = false;
        state.allocation.error = action.payload || 'An error occurred during allocation';
      });
  }
});

// Export actions
export const {
  setSelectedPath,
  clearSelectedPath,
  updatePortfolioIds,
  setCurrentStep,
  nextStep,
  previousStep,
  updateFormField,
  setFormData,
  resetFormData,
  setEditingMode,
  setMergedPortfolios,
  clearAllocationData,
      updateQuestionState,


} = pathSlice.actions;

// Selectors
export const selectCurrentPath = createSelector(
  (state) => state.path.selectedPathId,
  (state) => state.path.portfolioIds,
  (state) => state.path.isEditing,
  (state) => state.path.isDirty,
  (selectedPathId, portfolioIds, isEditing, isDirty) => ({
    pathId: selectedPathId,
    portfolioIds,
    isEditing,
    isDirty
  })
);

export const selectStepperState = createSelector(
  (state) => state.path.currentStep,
  (state) => state.path.formData,
  (currentStep, formData) => ({
    currentStep,
    formData,
  })
);

export const selectAllocationSummary = (state) => ({
  data: state.path.allocation.data,
    responses: state.path.allocation.responses,
  lastUpdated: state.path.allocation.lastUpdated,
  loading: state.path.allocation.loading,
  error: state.path.allocation.error,
  mergedPortfolios: state.path.allocation.mergedPortfolios

});


export const selectMergedPortfolios = (state) => state.path.allocation.mergedPortfolios;


export const selectFormField = (field) => (state) => state.path.formData[field];

export const selectQuestionState = (questionId) => (state) =>
  state.questionStates?.[questionId] || { completed: false, hasComponent: false };


export default pathSlice.reducer;